import { supabase } from "@/integrations/supabase/client";

const SESSION_TOKEN_KEY = 'fino_session_token';

export interface UserSession {
  id: string;
  user_id: string;
  session_token: string;
  device_info: {
    browser: string;
    os: string;
    device_type: 'desktop' | 'mobile' | 'tablet';
  };
  ip_address: string | null;
  location: {
    city: string;
    country: string;
    country_code: string;
  } | null;
  is_active: boolean;
  last_active_at: string;
  created_at: string;
  expires_at: string;
}

// Create a new session after login
export async function createSession(userId: string): Promise<{ success: boolean; sessionsDeactivated: number }> {
  try {
    const { data, error } = await supabase.functions.invoke('manage-session', {
      body: { action: 'create', user_id: userId }
    });

    if (error) {
      console.error('[sessionManager] Error creating session:', error);
      return { success: false, sessionsDeactivated: 0 };
    }

    if (data?.success && data?.session_token) {
      localStorage.setItem(SESSION_TOKEN_KEY, data.session_token);
      return { 
        success: true, 
        sessionsDeactivated: data.sessions_deactivated || 0 
      };
    }

    return { success: false, sessionsDeactivated: 0 };
  } catch (error) {
    console.error('[sessionManager] Exception creating session:', error);
    return { success: false, sessionsDeactivated: 0 };
  }
}

// Validate current session
// Uses "fail-open" strategy: on network errors, assume valid to prevent accidental logouts
export async function validateSession(): Promise<boolean> {
  const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
  if (!sessionToken) {
    // No token means user logged in before session management feature
    // Don't invalidate - backwards compatibility
    return true;
  }

  try {
    const { data, error } = await supabase.functions.invoke('manage-session', {
      body: { action: 'validate', session_token: sessionToken }
    });

    // On network/edge function errors, assume valid (fail-open)
    // This prevents logout on temporary network issues
    if (error) {
      console.warn('[sessionManager] Validation network error, assuming valid:', error.message);
      return true;
    }

    // Only logout if server explicitly says session is invalid
    if (data?.valid === false) {
      console.log('[sessionManager] Session explicitly invalidated by server:', data.reason);
      localStorage.removeItem(SESSION_TOKEN_KEY);
      return false;
    }

    return true;
  } catch (error) {
    console.error('[sessionManager] Exception validating session, assuming valid:', error);
    // Fail-open on exceptions - don't logout users due to transient errors
    return true;
  }
}

// Logout current session
export async function logoutSession(): Promise<void> {
  const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
  if (sessionToken) {
    try {
      await supabase.functions.invoke('manage-session', {
        body: { action: 'logout', session_token: sessionToken }
      });
    } catch (error) {
      console.error('[sessionManager] Error logging out session:', error);
    }
    localStorage.removeItem(SESSION_TOKEN_KEY);
  }
}

// Logout all other sessions
export async function logoutAllOtherSessions(userId: string): Promise<boolean> {
  const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
  try {
    const { data, error } = await supabase.functions.invoke('manage-session', {
      body: { 
        action: 'logout_all', 
        user_id: userId, 
        session_token: sessionToken // Exclude current session
      }
    });

    return !error && data?.success;
  } catch (error) {
    console.error('[sessionManager] Error logging out all sessions:', error);
    return false;
  }
}

// Logout a specific session by ID
export async function logoutSpecificSession(sessionId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_sessions')
      .update({ is_active: false })
      .eq('id', sessionId);

    return !error;
  } catch (error) {
    console.error('[sessionManager] Error logging out specific session:', error);
    return false;
  }
}

// Get all active sessions for current user
export async function getActiveSessions(userId: string): Promise<UserSession[]> {
  try {
    const { data, error } = await supabase
      .from('user_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[sessionManager] Error fetching sessions:', error);
      return [];
    }

    return (data || []) as UserSession[];
  } catch (error) {
    console.error('[sessionManager] Exception fetching sessions:', error);
    return [];
  }
}

// Get current session token
export function getCurrentSessionToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

// Check if a session is the current one
export function isCurrentSession(sessionToken: string): boolean {
  return sessionToken === localStorage.getItem(SESSION_TOKEN_KEY);
}
